import { memo, useMemo, useState, type CSSProperties, type ReactNode, type UIEvent } from "react";
import { Check, Plus } from "lucide-react";
import {
  Box,
  Group,
  MultiSelect,
  Select,
  Stack,
  Text,
  type ComboboxData,
  type ComboboxItem,
  type MantineSize,
  type OptionsFilter,
} from "@mantine/core";

import {
  buildLocationOptions,
  locationRefs,
  locationValue,
  locationValues,
  parseLocationValue,
  toSelectData,
  type LocationOption,
  type LocationPickerData,
  type LocationRef,
  type PickerKind,
  type PickerRoom,
} from "./locationPickerModel";
import { locationPath, locationPaths, type LocationPath } from "./locationPickerPath";
import {
  OPTION_RENDER_LIMIT,
  actionIndex,
  actionValue,
  filterPickerEntries,
  isNearBottom,
  loadingMoreItem,
  pickedOptionLabel,
  roomSearchTerm,
} from "./locationPickerPaging";
import { darkComboboxProps } from "./darkScheme";

/**
 * Rooms an app feeds the picker a page at a time (#472). The picker owns the
 * search box; it tells the source what to search for and when the list is
 * scrolled near its end. The source keeps the picked rooms among `rooms`
 * (fetched by id when no loaded page has them) so the closed field can name
 * them. No source: `rooms` is the whole list.
 */
export interface LocationPickerRoomSource {
  /** The rooms loaded so far, plus the picked ones. */
  rooms: readonly PickerRoom[];
  /** More pages remain: a "Loading more…" row ends the list. */
  hasMore?: boolean;
  /** The first page of the current list (or search) is on its way. */
  isLoading?: boolean;
  /** Loading failed; shown on the field and in the dropdown. */
  error?: ReactNode;
  /** Scrolled near the end of the list: fetch the next page. */
  loadMore?: () => void;
  /** The text to search the server for ("" = browse). Never the picked
   *  label a closed Select shows. Debouncing is the source's job. */
  search?: (term: string) => void;
  /** Encoded values (`r:12`) the server found for the current search: shown
   *  even where the picker's own word rules would not match them. */
  serverHits?: ReadonlySet<string>;
}

/** A row after the options ("+ Create room"): picking it calls `onClick`
 *  and leaves the value as it was. */
export interface LocationPickerAction {
  label: string;
  onClick: () => void;
  /** Defaults to a plus. */
  icon?: ReactNode;
}

interface LocationPickerBaseProps extends LocationPickerData {
  /** Which levels can be picked: `['room']`, `['floor']`, `['address','floor']`,
   *  `['address','floor','room']` for a scope, `['door']` for a door… Each is grouped by the level above. */
  kinds: readonly PickerKind[];
  /** Paged rooms (see `LocationPickerRoomSource`); replaces `rooms`. */
  roomSource?: LocationPickerRoomSource;
  label?: ReactNode;
  /** The field's accessible name when it has no visible `label`. */
  ariaLabel?: string;
  description?: ReactNode;
  /** A validation error (a form's). */
  error?: ReactNode;
  /** The lists passed in failed to load ("Couldn't load rooms"). */
  loadError?: ReactNode;
  placeholder?: string;
  required?: boolean;
  disabled?: boolean;
  /** Shows an inline clear (x). */
  clearable?: boolean;
  /** The clear (x)'s accessible name. Mantine hides the button from assistive
   *  technology unless it is named. */
  clearLabel?: string;
  /** The lists passed in are still loading. */
  isLoading?: boolean;
  nothingFoundMessage?: string;
  /** An extra line under a room's row (e.g. who rents it). */
  roomHint?: (room: PickerRoom | undefined) => ReactNode;
  /** `dark`: over a floorplan canvas — the theme's own dark scheme for this
   *  field and its dropdown, whatever the app's scheme is. */
  variant?: "default" | "dark";
  /** Rows after the options, e.g. "+ Create room" in the floorplan editor. */
  actions?: readonly LocationPickerAction[];
  size?: MantineSize;
}

export interface LocationPickerSingleProps extends LocationPickerBaseProps {
  multiple?: false;
  value: LocationRef | null;
  /** `path`: the pick's address / floor (and room), for forms and filters
   *  that store the parent ids too. */
  onChange: (value: LocationRef | null, path: LocationPath | null) => void;
}

export interface LocationPickerMultipleProps extends LocationPickerBaseProps {
  multiple: true;
  value: LocationRef[];
  /** `paths[i]` is where `value[i]` sits (see the single mode). */
  onChange: (value: LocationRef[], paths: LocationPath[]) => void;
}

export type LocationPickerProps = LocationPickerSingleProps | LocationPickerMultipleProps;

const ROW_INDENT = 16;
const SINGLE_INPUT: CSSProperties = { textOverflow: "ellipsis" };
const MAX_DROPDOWN_HEIGHT = 320;

// `dark`: Mantine's dark scheme scoped to the field and its portalled dropdown.
const DARK_STYLES = darkComboboxProps.styles;
const DARK_SINGLE_STYLES = { ...DARK_STYLES, input: SINGLE_INPUT };
const SINGLE_STYLES = { input: SINGLE_INPUT };

const COMBOBOX_PROPS = { keepMounted: false } as const;

/**
 * One location row of the dropdown. Memoised: while text is typed the list
 * re-renders on every key, and a row whose option, tick and hint are the
 * same as before is not rendered again (`row` is the model's own object,
 * stable while the lists are). A room hint is a new node each time, so rows
 * with hints always render.
 */
const LocationRow = memo(function LocationRow({
  row,
  checked,
  hint,
}: {
  row: LocationOption;
  /** Multiple mode: whether the row is ticked; undefined in single mode (no tick column). */
  checked: boolean | undefined;
  hint: ReactNode;
}) {
  return (
    <Group gap="xs" wrap="nowrap" pl={row.depth * ROW_INDENT} miw={0}>
      {checked !== undefined && (checked ? <Check size={14} /> : <Box w={14} />)}
      <Stack gap={0} miw={0} flex={1}>
        <Group gap={6} wrap="nowrap" miw={0}>
          <Text fz="sm" truncate>{row.primary}</Text>
          {/* Truncates too: on a narrow screen a long number ran past the dropdown edge. */}
          {row.secondary && <Text fz="xs" c="dimmed" truncate miw={0}>{row.secondary}</Text>}
        </Group>
        {hint}
      </Stack>
    </Group>
  );
});

const isMultiple = (p: LocationPickerProps): p is LocationPickerMultipleProps => p.multiple === true;

/**
 * The grouped location picker (#472): addresses, floors, rooms and doors in ONE
 * searchable select, each level grouped under the level above — rooms and doors
 * under "Lunta Street 26 · Floor 6", floors under their address. Single choice is
 * a Mantine `Select`; `multiple` a `MultiSelect` where a whole address, a
 * floor (the floor itself, not its rooms) or single rooms can be ticked.
 *
 * Presentational: the lists come in as props (`addresses`, `floors`,
 * `rooms`, `doors`), or rooms page by page through `roomSource`. Each app fetches its
 * own data; the grouping, labels and search rules live in
 * `locationPickerModel`.
 */
export function LocationPicker(props: LocationPickerProps) {
  const {
    kinds,
    label,
    ariaLabel,
    description,
    error,
    loadError,
    placeholder,
    required,
    disabled,
    clearable,
    clearLabel,
    isLoading,
    nothingFoundMessage,
    roomHint,
    addresses,
    floors,
    rooms,
    doors,
    roomSource,
    variant = "default",
    actions,
    size,
  } = props;
  // Search text is ours, so an "add a room" picker whose value stays empty
  // after a pick doesn't keep the old search typed in.
  const [search, setSearch] = useState("");
  // The single pick's label, kept while a room picked off a search page is
  // fetched again by id (it isn't an option until then).
  const [lastPicked, setLastPicked] = useState<{ value: string; label: string } | null>(null);

  const paged = !!roomSource && kinds.includes("room");
  const data = useMemo<LocationPickerData>(
    () => ({ addresses, floors, rooms: paged ? roomSource.rooms : rooms, doors }),
    [addresses, floors, rooms, doors, paged, roomSource?.rooms],
  );
  const loading = !!isLoading || (paged && !!roomSource.isLoading);
  const failed = loadError ?? (paged ? roomSource.error : null) ?? null;

  const groups = useMemo(() => buildLocationOptions(kinds, data), [kinds, data]);
  const byValue = useMemo(
    () => new Map(groups.flatMap((g) => g.items.map((item) => [item.value, item] as const))),
    [groups],
  );
  const roomById = useMemo(() => new Map((data.rooms ?? []).map((r) => [String(r.id), r])), [data.rooms]);
  const hasMore = paged && !!roomSource.hasMore;
  const selectData = useMemo<ComboboxData>(() => {
    const base = toSelectData(groups) as ComboboxData;
    const more = hasMore ? [loadingMoreItem()] : [];
    const actionRows = (actions ?? []).map((a, i): ComboboxItem => ({ value: actionValue(i), label: a.label }));
    return [...base, ...more, ...actionRows];
  }, [groups, hasMore, actions]);

  // Mantine's own filter matches the label; ours matches every level's
  // words, keeps the server's room hits, and caps a list that isn't typed in.
  const filter: OptionsFilter = ({ options, search: text, limit }) =>
    filterPickerEntries(options, text, limit, {
      options: [...byValue.values()],
      serverHits: paged ? roomSource.serverHits : undefined,
    });

  const pickedLabel = (): string | undefined =>
    isMultiple(props) || !props.value
      ? undefined
      : pickedOptionLabel(locationValue(props.value), (v) => byValue.get(v)?.label, lastPicked);
  const sendSearch = (text: string) => {
    if (paged) roomSource.search?.(text);
  };
  const onSearchChange = (text: string) => {
    setSearch(text);
    sendSearch(roomSearchTerm(text, pickedLabel()));
  };
  /** An action row was picked: run it, keep the value. True when it was one. */
  const runAction = (value: string | null | undefined): boolean => {
    const index = value == null ? null : actionIndex(value);
    if (index == null) return false;
    // The value stays, so a single Select goes back to showing its pick.
    setSearch(pickedLabel() ?? "");
    sendSearch("");
    actions?.[index]?.onClick();
    return true;
  };

  const onScroll = (event: UIEvent<HTMLDivElement>) => {
    if (hasMore && isNearBottom(event.currentTarget)) roomSource?.loadMore?.();
  };

  const renderRow = (option: ComboboxItem, checked: boolean | undefined) => {
    const action = actionIndex(option.value);
    if (action != null) {
      return (
        <Group gap="xs" wrap="nowrap" c="blue">
          {actions?.[action]?.icon ?? <Plus size={14} />}
          <Text fz="sm" c="inherit">{option.label}</Text>
        </Group>
      );
    }
    const row: LocationOption | undefined = byValue.get(option.value);
    if (!row) return <Text fz="sm" c="dimmed">{option.label}</Text>;
    const hint = row.kind === "room" && roomHint ? roomHint(roomById.get(String(row.id))) : null;
    return <LocationRow row={row} checked={props.multiple ? !!checked : undefined} hint={hint} />;
  };

  const dark = variant === "dark";
  const shared = {
    label,
    "aria-label": ariaLabel,
    description,
    required,
    disabled,
    clearable,
    // Mantine's clear (x) is aria-hidden; named, it's a button assistive technology can find.
    clearButtonProps: clearLabel ? { "aria-label": clearLabel, "aria-hidden": false } : undefined,
    size,
    data: selectData,
    filter,
    searchable: true,
    maxDropdownHeight: MAX_DROPDOWN_HEIGHT,
    // Mantine renders every option: cap a list that isn't paged (#472 S5).
    limit: paged ? undefined : OPTION_RENDER_LIMIT,
    // Scrolled near the end: the next page of rooms.
    scrollAreaProps: hasMore ? { viewportProps: { onScroll } } : undefined,
    // Mantine keeps a closed dropdown's options mounted (hidden) by default, so
    // every render of the form around the field rendered up to 200 rows again:
    // a keystroke in another field, a pan of the floorplan under the finder.
    // Closed, nothing is rendered; the rows mount when it opens.
    comboboxProps: COMBOBOX_PROPS,
    // A failed fetch says so (on the field and in the dropdown), never "nothing found".
    error: error ?? failed ?? undefined,
    nothingFoundMessage: failed ?? (loading ? "Loading…" : (nothingFoundMessage ?? "Nothing found")),
    searchValue: search,
    onSearchChange,
    attributes: dark ? darkComboboxProps.attributes : undefined,
  };

  if (isMultiple(props)) {
    const { value, onChange } = props;
    return (
      <MultiSelect
        {...shared}
        styles={dark ? DARK_STYLES : undefined}
        placeholder={value.length ? undefined : placeholder}
        value={locationValues(value)}
        onChange={(values) => {
          if (values.some((v) => runAction(v))) return;
          const refs = locationRefs(values);
          onChange(refs, locationPaths(refs, data));
        }}
        renderOption={({ option, checked }) => renderRow(option, checked)}
        hidePickedOptions={false}
      />
    );
  }

  const { value, onChange } = props as LocationPickerSingleProps;
  const picked = value ? byValue.get(locationValue(value)) : undefined;
  return (
    <Select
      {...shared}
      // A long room label ("Townhouse · 26605 — Lunta Street 26, Floor 6")
      // ends in an ellipsis on a narrow screen; the whole of it is the hover title.
      title={picked?.label}
      styles={dark ? DARK_SINGLE_STYLES : SINGLE_STYLES}
      placeholder={loading ? "Loading…" : placeholder}
      value={value ? locationValue(value) : null}
      onChange={(next) => {
        if (runAction(next)) return;
        setSearch("");
        sendSearch("");
        const pickedRow = next ? byValue.get(next) : undefined;
        setLastPicked(next && pickedRow ? { value: next, label: pickedRow.label } : null);
        const ref = next ? parseLocationValue(next) : null;
        onChange(ref, ref ? locationPath(ref, data) : null);
      }}
      renderOption={({ option, checked }) => renderRow(option, checked)}
      allowDeselect={false}
    />
  );
}
